import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const ENDPOINT_MAP = {
  panchang: { path: '/api/v3/vedic/panchang', icon: '🌙', title: 'Panchang', description: 'Tithi, Karana, Yoga, Nakshatra, Vaar' },
  doshas: { multi: ['manglik-dosha', 'kaal-sarpa-dosha', 'sade-sati'], icon: '⚖️', title: 'Doshas Analysis' },
  yogas: { path: '/api/v3/vedic/yoga-analysis', icon: '✨', title: 'Yoga Analysis', description: '21+ classical yogas detected' },
  shadbala: { path: '/api/v3/vedic/shadbala', icon: '💪', title: 'Shadbala', description: '6-fold planetary strength' },
  ashtakvarga: { path: '/api/v3/vedic/ashtakvarga', icon: '🎯', title: 'Ashtakvarga', description: 'Bindu calculations + SAV' },
  remedies: { path: '/api/v3/vedic/remedies', icon: '🪔', title: 'Remedies', description: 'Mantras, gemstones, donations' }
};

function FeatureTab({ view, chartData, birthInfo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!birthInfo) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const config = ENDPOINT_MAP[view];
        if (!config) throw new Error('Unknown view');

        const body = {
          subject: {
            name: 'You',
            birth_data: {
              year: parseInt(birthInfo.date.split('-')[0]),
              month: parseInt(birthInfo.date.split('-')[1]),
              day: parseInt(birthInfo.date.split('-')[2]),
              hour: parseInt(birthInfo.time.split(':')[0]),
              minute: parseInt(birthInfo.time.split(':')[1]),
              second: 0,
              latitude: parseFloat(birthInfo.latitude),
              longitude: parseFloat(birthInfo.longitude)
            }
          },
          options: { ayanamsa: 'lahiri' }
        };

        if (config.multi) {
          // Fetch multiple endpoints in parallel
          const results = {};
          await Promise.all(config.multi.map(async (endpoint) => {
            const res = await fetch(`${API_BASE}/api/v3/vedic/${endpoint}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            });
            const json = await res.json();
            if (json.success) results[endpoint] = json.data;
          }));
          setData(results);
        } else {
          const res = await fetch(`${API_BASE}${config.path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
          const json = await res.json();
          if (json.success) setData(json.data);
          else setError(json.error || 'Failed to load');
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [view, birthInfo]);

  const config = ENDPOINT_MAP[view];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-4xl mb-3">{config?.icon || '⏳'}</div>
          <p className="text-white/60">Loading {config?.title || view}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-6">
        <p className="text-red-300">⚠️ {error}</p>
      </div>
    );
  }

  // Render specific view
  if (view === 'panchang') return <PanchangView data={data} />;
  if (view === 'doshas') return <DoshasView data={data} />;
  if (view === 'yogas') return <YogasView data={data} />;
  if (view === 'shadbala') return <ShadbalaView data={data} />;
  if (view === 'ashtakvarga') return <AshtakvargaView data={data} />;
  if (view === 'remedies') return <RemediesView data={data} />;

  return null;
}

// ========== VIEWS ==========

function PanchangView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">🌙 Panchang at Birth</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card title="Tithi" subtitle={`#${data.tithi?.number} • ${data.tithi?.paksha}`} value={data.tithi?.name} desc={data.tithi?.description} />
        <Card title="Nakshatra" subtitle={`Lord: ${data.nakshatra?.lord} • Pada ${data.nakshatra?.pada}`} value={data.nakshatra?.name} desc={`${data.nakshatra?.completion_percent}% complete`} />
        <Card title="Yoga" subtitle={`#${data.yoga?.number} • ${data.yoga?.nature}`} value={data.yoga?.name} desc={data.yoga?.description} />
        <Card title="Karana" subtitle={`#${data.karana?.number} • ${data.karana?.type}`} value={data.karana?.name} desc={data.karana?.description} />
        <Card title="Vaar (Day)" subtitle={`Lord: ${data.vaar?.lord}`} value={data.vaar?.name} desc={data.vaar?.sanskrit_name} />
        <Card title="Hora" subtitle={`Lord: ${data.hora?.lord}`} value={`#${data.hora?.number}`} desc={`${data.hora?.type} hora`} />
        <Card title="Rahukaal" subtitle="Inauspicious time" value={`${data.rahukaal?.start} - ${data.rahukaal?.end}`} desc="Avoid new ventures" />
        <Card title="Sunrise / Sunset" subtitle="" value={`${data.sunrise} / ${data.sunset}`} desc="Approximate" />
      </div>
    </div>
  );
}

function DoshasView({ data }) {
  if (!data) return null;
  const m = data['manglik-dosha'];
  const k = data['kaal-sarpa-dosha'];
  const s = data['sade-sati'];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">⚖️ Doshas Analysis</h2>

      {/* Manglik */}
      <DoshaCard
        title="🔴 Manglik Dosha"
        active={m?.is_manglik}
        severity={m?.severity}
        details={[
          `Mars in House ${m?.mars_house_from_lagna} from Lagna`,
          `Mars in House ${m?.mars_house_from_moon} from Moon`,
          `Mars in ${m?.mars_rashi}`,
          ...(m?.cancellation_factors || []).map(c => `Cancellation: ${c}`)
        ]}
        description={m?.description}
        remedies={m?.remedies}
      />

      {/* Kaal Sarpa */}
      <DoshaCard
        title="🐍 Kaal Sarpa Dosha"
        active={k?.is_present}
        severity={k?.intensity}
        details={[
          `Type: ${k?.type || 'None'}`,
          `Rahu in House ${k?.rahu?.house}`,
          `Ketu in House ${k?.ketu?.house}`,
          `Planets with Rahu: ${k?.planets_with_rahu?.join(', ') || 'none'}`,
          `Planets with Ketu: ${k?.planets_with_ketu?.join(', ') || 'none'}`
        ]}
        description={k?.description}
        remedies={k?.remedies}
      />

      {/* Sade Sati - WITH PHASE DATES */}
      <div className={`border rounded-lg p-4 ${s?.is_active ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-green-500/10 border-green-500/30'}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold">🪐 Sade Sati</h3>
          <span className={`text-xs px-3 py-1 rounded font-semibold ${s?.is_active ? 'bg-yellow-500/30 text-yellow-200' : 'bg-green-500/30 text-green-200'}`}>
            {s?.is_active ? `Active - ${s.current_phase} (${s.intensity})` : 'Not Active'}
          </span>
        </div>
        <p className="text-sm text-white/80 mb-3">{s?.description}</p>
        <div className="text-xs text-white/60 mb-4 space-y-1">
          <div>• Natal Moon: <strong>{s?.natal_moon?.sign}</strong></div>
          <div>• Current Saturn: <strong>{s?.current_saturn?.sign}</strong></div>
          <div>• Saturn is in house {s?.saturn_house_from_natal_moon} from natal Moon</div>
          <div>• Total duration: <strong>{s?.total_duration}</strong></div>
        </div>

        {/* PHASE TIMELINE */}
        {s?.phase_dates && s.phase_dates.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold text-purple-300 uppercase tracking-wide">📅 Phase Timeline</p>
            {s.phase_dates.map((p, i) => (
              <div key={i} className={`p-3 rounded-lg border ${p.currently_active ? 'bg-purple-500/20 border-purple-400' : 'bg-slate-800/40 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">{p.name}</span>
                  {p.currently_active && <span className="text-xs px-2 py-0.5 bg-purple-500 text-white rounded animate-pulse">▶ NOW</span>}
                </div>
                <div className="text-xs text-white/70 mb-1">
                  📍 Saturn transits <strong>{p.sign}</strong>
                </div>
                <div className="text-xs text-white/60">
                  📆 {p.approximate_start} → {p.approximate_end} ({p.duration_years} years)
                </div>
                <div className="text-xs text-white/50 mt-1 italic">{p.intensity}</div>
              </div>
            ))}
          </div>
        )}

        {s?.is_active && s?.remedies?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <p className="text-xs font-semibold text-purple-300 mb-1">Remedies:</p>
            <ul className="text-xs text-white/70 space-y-0.5">
              {s.remedies.slice(0, 4).map((r, i) => <li key={i}>🪔 {r}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function YogasView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">✨ Yogas in Your Chart</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <p className="text-xs text-white/60">Total</p>
          <p className="text-2xl font-bold">{data.total_count}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <p className="text-xs text-green-300">Auspicious</p>
          <p className="text-2xl font-bold text-green-400">{data.auspicious_count}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-xs text-red-300">Challenging</p>
          <p className="text-2xl font-bold text-red-400">{data.inauspicious_count}</p>
        </div>
      </div>
      <div className="space-y-3">
        {(data.yogas || []).map((y, i) => (
          <div key={i} className={`border rounded-lg p-4 ${y.nature === 'auspicious' ? 'bg-green-500/5 border-green-500/30' : y.nature === 'inauspicious' ? 'bg-red-500/5 border-red-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold">{y.name}</h3>
              <div className="flex gap-2">
                <span className="text-xs px-2 py-1 bg-slate-700 rounded">Strength: {y.strength}</span>
                <span className={`text-xs px-2 py-1 rounded ${y.nature === 'auspicious' ? 'bg-green-500/30 text-green-200' : 'bg-red-500/30 text-red-200'}`}>{y.nature}</span>
              </div>
            </div>
            <p className="text-sm text-white/70 mb-2">{y.description}</p>
            <p className="text-xs text-white/50">Planets: {y.planets_involved?.join(', ')}</p>
            {y.effects?.positive?.length > 0 && (
              <ul className="mt-2 text-xs text-white/70">
                {y.effects.positive.slice(0, 3).map((e, j) => <li key={j}>✓ {e}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ShadbalaView({ data }) {
  if (!data?.shadbala) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">💪 Shadbala (Planetary Strength)</h2>
      <p className="text-white/60 text-sm">Strongest: <strong>{data.strongest_planet}</strong> | Weakest: <strong>{data.weakest_planet}</strong></p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data.shadbala).map(([planet, info]) => (
          <div key={planet} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">{planet}</h3>
              <span className={`text-xs px-2 py-1 rounded ${info.is_strong ? 'bg-green-500/30 text-green-200' : 'bg-yellow-500/30 text-yellow-200'}`}>
                {info.strength_description}
              </span>
            </div>
            <div className="text-xs text-white/60 mb-2">{info.sign} • House {info.house}</div>
            <div className="space-y-1 text-xs">
              {Object.entries(info.components).map(([comp, val]) => (
                <div key={comp} className="flex justify-between">
                  <span className="text-white/60 capitalize">{comp.replace('_', ' ')}</span>
                  <span>{val.value} / {val.max}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between font-semibold">
              <span>Total</span>
              <span>{info.total} ({info.rupas} rupas)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AshtakvargaView({ data }) {
  if (!data?.bhinnashtakvarga) return null;
  const sav = data.sarvashtakvarga;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">🎯 Ashtakvarga</h2>
      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
        <h3 className="font-bold mb-3">Sarvashtakvarga (SAV) - Total: {sav?.total}</h3>
        <p className="text-sm text-white/60 mb-3">Strongest sign: <strong>{sav?.strongest_sign}</strong> | Weakest: <strong>{sav?.weakest_sign}</strong></p>
        <div className="grid grid-cols-12 gap-1">
          {sav?.sign_breakdown?.map((s, i) => (
            <div key={i} className="text-center bg-slate-900 p-2 rounded">
              <div className="text-xs text-white/50">{s.sign}</div>
              <div className="font-bold">{s.bindus}</div>
            </div>
          ))}
        </div>
      </div>

      <h3 className="font-bold mt-4">Bhinnashtakvarga (Per Planet)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(data.bhinnashtakvarga).map(([planet, info]) => (
          <div key={planet} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
            <div className="flex justify-between mb-2">
              <span className="font-bold">{planet} in {info.current_sign}</span>
              <span className="text-sm">{info.bindus_in_current_sign} bindus ({info.strength})</span>
            </div>
            <div className="text-xs text-white/60">Total: {info.total_chart_bindus}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RemediesView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">🪔 Remedies for Weak Planets</h2>
      <p className="text-sm text-white/60">{data.general_advice}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.remedies || []).map((r, i) => (
          <div key={i} className="bg-slate-800/50 border border-purple-500/30 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">{r.planet}</h3>
              <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded">{r.weakness}</span>
            </div>
            {r.mantra && <p className="text-sm mb-2"><strong>Mantra:</strong> <span className="text-purple-300 italic">"{r.mantra}"</span></p>}
            {r.gemstone && <p className="text-sm mb-2"><strong>Gemstone:</strong> {r.gemstone}</p>}
            {r.donation && <p className="text-sm mb-2"><strong>Donation:</strong> {r.donation}</p>}
            {r.day && <p className="text-sm mb-2"><strong>Best day:</strong> {r.day}</p>}
            {r.deity && <p className="text-sm"><strong>Worship:</strong> {r.deity}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== HELPERS ==========

function Card({ title, subtitle, value, desc }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
      <div className="text-xs text-white/50 mb-1">{title}</div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      {subtitle && <div className="text-xs text-purple-300">{subtitle}</div>}
      {desc && <div className="text-xs text-white/60 mt-2">{desc}</div>}
    </div>
  );
}

function DoshaCard({ title, active, severity, details, description, remedies }) {
  return (
    <div className={`border rounded-lg p-4 ${active ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-green-500/10 border-green-500/30'}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-bold">{title}</h3>
        <span className={`text-xs px-3 py-1 rounded font-semibold ${active ? 'bg-yellow-500/30 text-yellow-200' : 'bg-green-500/30 text-green-200'}`}>
          {active ? `Present (${severity || 'mild'})` : 'Not Present'}
        </span>
      </div>
      <p className="text-sm text-white/80 mb-3">{description}</p>
      <div className="space-y-1 mb-3">
        {details?.filter(Boolean).map((d, i) => (
          <div key={i} className="text-xs text-white/60">• {d}</div>
        ))}
      </div>
      {active && remedies?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <p className="text-xs font-semibold text-purple-300 mb-1">Remedies:</p>
          <ul className="text-xs text-white/70 space-y-0.5">
            {remedies.slice(0, 4).map((r, i) => <li key={i}>🪔 {r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FeatureTab;
